import {ActionState, CurrentState, PlayerColor, TurnState} from '@colonist/game-states'
import {GameModeTranslationKeyContext, TranslationObject} from '@colonist/i18n'
import {ArrayUtils} from '@colonist/utils'

type CurrentStateMessageData =
    {
        type: 'turn-state-without-trade'
        arePlayersTrading: false
        amITakingAction: boolean
        playerDisplayUsername: string
    }
    | {
        type: 'turn-state-ongoing-trade-in-my-turn'
        arePlayersTrading: true
        amICurrentTurnPlayer: true
        wasATradeAccepted: boolean
        numberOtherPlayersTrading: number
        otherPlayerDisplayName: string
    }
    | {
        type: 'turn-state-ongoing-trade-in-others-turn'
        arePlayersTrading: true
        amICurrentTurnPlayer: false
        amITakingAction: boolean
        numberOfPlayersTakingAction: number
        playerDisplayUsername: string
    }

export type CurrentStateMessageInput =
    {
        type: 'turn-state-default'
        currentState: CurrentState
        amITakingAction: boolean
        playerDisplayUsername: string
    }
    | {
        type: 'turn-state-turn'
        currentState: CurrentState
        amITakingAction: boolean
        playersTakingAction: PlayerColor[]
        playerDisplayUsername: string
        numberOfPlayersTakingAction: number
        arePlayersTrading: boolean
        myColor: PlayerColor
        wasATradeAccepted: boolean
        otherPlayerDisplayName: string
    }
    | {
        type: 'action-state'
        currentState: CurrentState
        actionState: ActionState
        playersTakingAction: PlayerColor[]
        myColor: PlayerColor
        translationKeyContext: GameModeTranslationKeyContext
        displayUsername: string
    }

export class CurrentStateMessageUtils {
    static getCurrentStateMessage(input: CurrentStateMessageInput): TranslationObject {
        switch(input.type) {
            case 'action-state':
                return CurrentStateMessageUtils.getActionStateMessage(input)
            case 'turn-state-default':
            case 'turn-state-turn':
                return CurrentStateMessageUtils.getTurnStateMessage(input)
            default:
                return {key: 'strings:colonistCommon.utils.bug'}
        }
    }

    private static getActionStateMessage(input: CurrentStateMessageInput): TranslationObject {
        if(input.type != 'action-state') return {key: 'strings:colonistCommon.utils.bug'}

        const amITakingAction = input.playersTakingAction.includes(input.myColor)
        const numberOfPlayersTakingAction = input.playersTakingAction.length

        switch(input.actionState) {
            case ActionState.InitialPlacementPlaceSettlement:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.initialPlacementPlaceSettlementSelf'} :
                    {key: 'strings:game.gameState.actions.initialPlacementPlaceSettlementOther', options: {playerUsername: input.displayUsername}}
            case ActionState.InitialPlacementPlaceCity:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.initialPlacementPlaceCitySelf'} :
                    {key: 'strings:game.gameState.actions.initialPlacementPlaceCityOther', options: {playerUsername: input.displayUsername}}
            case ActionState.InitialPlacementRoadPlacement:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.initialPlacementRoadPlacementSelf', options: {context: input.translationKeyContext}} :
                    {key: 'strings:game.gameState.actions.initialPlacementRoadPlacementOther', options: {context: input.translationKeyContext, playerUsername: input.displayUsername}}
            case ActionState.PlaceRoad:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeRoadSelf'} :
                    {key: 'strings:game.gameState.actions.placeRoadOther', options: {playerUsername: input.displayUsername}}
            case ActionState.PlaceSettlement:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeSettlementSelf'} :
                    {key: 'strings:game.gameState.actions.placeSettlementOther', options: {playerUsername: input.displayUsername}}
            case ActionState.PlaceCity:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeCitySelf'} :
                    {key: 'strings:game.gameState.actions.placeCityOther', options: {playerUsername: input.displayUsername}}
            case ActionState.PlaceShip:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeShipSelf'} :
                    {key: 'strings:game.gameState.actions.placeShipOther', options: {playerUsername: input.displayUsername}}
            case ActionState.SelectShipToMove:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectShipToMoveSelf'} :
                    {key: 'strings:game.gameState.actions.selectShipToMoveOther', options: {playerUsername: input.displayUsername}}
            case ActionState.MoveShip:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.moveShipSelf'} :
                    {key: 'strings:game.gameState.actions.moveShipOther', options: {playerUsername: input.displayUsername}}
            case ActionState.PlaceCityWall:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeCityWallSelf'} :
                    {key: 'strings:game.gameState.actions.placeCityWallOther', options: {playerUsername: input.displayUsername}}
            case ActionState.PlaceKnight:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeKnightSelf'} :
                    {key: 'strings:game.gameState.actions.placeKnightOther', options: {playerUsername: input.displayUsername}}
            case ActionState.UpgradeKnight:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.upgradeKnightSelf'} :
                    {key: 'strings:game.gameState.actions.upgradeKnightOther', options: {playerUsername: input.displayUsername}}
            case ActionState.ActivateKnight:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.activateKnightSelf'} :
                    {key: 'strings:game.gameState.actions.activateKnightOther', options: {playerUsername: input.displayUsername}}
            case ActionState.SelectKnightToTakeAction:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectKnightToTakeActionSelf'} :
                    {key: 'strings:game.gameState.actions.selectKnightToTakeActionOther', options: {playerUsername: input.displayUsername}}
            case ActionState.SelectKnightAction:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectKnightActionSelf'} :
                    {key: 'strings:game.gameState.actions.selectKnightActionOther', options: {playerUsername: input.displayUsername}}
            case ActionState.PlaceRoadForFree:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeRoadForFreeSelf'} :
                    {key: 'strings:game.gameState.actions.placeRoadForFreeOther', options: {playerUsername: input.displayUsername}}
            case ActionState.PlaceShipForFree:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeShipForFreeSelf'} :
                    {key: 'strings:game.gameState.actions.placeShipForFreeOther', options: {playerUsername: input.displayUsername}}
            case ActionState.PlaceCityWithDiscount:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeCityWithDiscountSelf'} :
                    {key: 'strings:game.gameState.actions.placeCityWithDiscountOther', options: {playerUsername: input.displayUsername}}
            case ActionState.PlaceCityWallForFree:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeCityWallForFreeSelf'} :
                    {key: 'strings:game.gameState.actions.placeCityWallForFreeOther', options: {playerUsername: input.displayUsername}}
            case ActionState.PlaceKnightForFree:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeKnightForFreeSelf'} :
                    {key: 'strings:game.gameState.actions.placeKnightForFreeOther', options: {playerUsername: input.displayUsername}}
            case ActionState.UpgradeKnightForFree:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.upgradeKnightForFreeSelf'} :
                    {key: 'strings:game.gameState.actions.upgradeKnightForFreeOther', options: {playerUsername: input.displayUsername}}
            case ActionState.MoveKnightOffTurn:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.moveKnightOffTurnSelf'} :
                    {key: 'strings:game.gameState.actions.moveKnightOffTurnOther', options: {playerUsername: input.displayUsername}}
            case ActionState.SelectKnightToRemove:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectKnightToRemoveSelf'} :
                    {key: 'strings:game.gameState.actions.selectKnightToRemoveOther', options: {playerUsername: input.displayUsername}}
            case ActionState.PlaceRobberOnly:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeRobberOnlySelf'} :
                    {key: 'strings:game.gameState.actions.placeRobberOnlyOther', options: {playerUsername: input.displayUsername}}
            case ActionState.PlacePirateOnly:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placePirateOnlySelf'} :
                    {key: 'strings:game.gameState.actions.placePirateOnlyOther', options: {playerUsername: input.displayUsername}}
            case ActionState.PlaceRobberOrPirate:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeRobberOrPirateSelf', options: {context: input.translationKeyContext}} :
                    {key: 'strings:game.gameState.actions.placeRobberOrPirateOther', options: {context: input.translationKeyContext, playerUsername: input.displayUsername}}
            case ActionState.SelectWhoToRob:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectWhoToRobSelf'} :
                    {key: 'strings:game.gameState.actions.selectWhoToRobOther', options: {playerUsername: input.displayUsername}}
            case ActionState.SelectCardsToDiscard:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectCardsToDiscardSelf', options: {count: numberOfPlayersTakingAction}} :
                    {key: 'strings:game.gameState.actions.selectCardsToDiscardOther', options: {count: numberOfPlayersTakingAction, playerUsername: input.displayUsername}}
            case ActionState.SelectProgressCardsToDiscard:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectProgressCardsToDiscardSelf', options: {count: numberOfPlayersTakingAction}} :
                    {key: 'strings:game.gameState.actions.selectProgressCardsToDiscardOther', options: {count: numberOfPlayersTakingAction, playerUsername: input.displayUsername}}
            case ActionState.Place2MoreRoadBuilding:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.place2MoreRoadBuildingSelf'} :
                    {key: 'strings:game.gameState.actions.place2MoreRoadBuildingOther', options: {playerUsername: input.displayUsername}}
            case ActionState.Place1MoreRoadBuilding:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.place1MoreRoadBuildingSelf'} :
                    {key: 'strings:game.gameState.actions.place1MoreRoadBuildingOther', options: {playerUsername: input.displayUsername}}
            case ActionState.Select2ResourcesForYearOfPlenty:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.select2ResourcesForYearOfPlentySelf'} :
                    {key: 'strings:game.gameState.actions.select2ResourcesForYearOfPlentyOther', options: {playerUsername: input.displayUsername}}
            case ActionState.Select1ResourceForMonopoly:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.select1ResourceForMonopolySelf'} :
                    {key: 'strings:game.gameState.actions.select1ResourceForMonopolyOther', options: {playerUsername: input.displayUsername}}
            case ActionState.SelectCardsForPendingDistributions:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectCardsForPendingDistributionsSelf', options: {count: numberOfPlayersTakingAction}} :
                    {key: 'strings:game.gameState.actions.selectCardsForPendingDistributionsOther', options: {count: numberOfPlayersTakingAction, playerUsername: input.displayUsername}}
            case ActionState.PlaceMetropolis:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeMetropolisSelf'} :
                    {key: 'strings:game.gameState.actions.placeMetropolisOther', options: {playerUsername: input.displayUsername}}
            case ActionState.SelectProgressCard:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectProgressCardSelf', options: {count: numberOfPlayersTakingAction}} :
                    {key: 'strings:game.gameState.actions.selectProgressCardOther', options: {count: numberOfPlayersTakingAction, playerUsername: input.displayUsername}}
            case ActionState.SelectCityToPillage:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectCityToPillageSelf'} :
                    {key: 'strings:game.gameState.actions.selectCityToPillageOther', options: {playerUsername: input.displayUsername}}
            case ActionState.PlaceMerchant:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeMerchantSelf'} :
                    {key: 'strings:game.gameState.actions.placeMerchantOther', options: {playerUsername: input.displayUsername}}
            case ActionState.SelectDiceForAlchemist:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectDiceForAlchemistSelf'} :
                    {key: 'strings:game.gameState.actions.selectDiceForAlchemistOther', options: {playerUsername: input.displayUsername}}
            case ActionState.SelectTileProductionNumbersToSwap:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectTileProductionNumbersToSwapSelf'} :
                    {key: 'strings:game.gameState.actions.selectTileProductionNumbersToSwapOther', options: {playerUsername: input.displayUsername}}
            case ActionState.SelectPlayerForDeserter:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectPlayerForDeserterSelf'} :
                    {key: 'strings:game.gameState.actions.selectPlayerForDeserterOther', options: {playerUsername: input.displayUsername}}
            case ActionState.SelectRoadToRemove:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectRoadToRemoveSelf'} :
                    {key: 'strings:game.gameState.actions.selectRoadToRemoveOther', options: {playerUsername: input.displayUsername}}
            case ActionState.DiplomatSelectWhetherToPlaceNewEdgePiece:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.diplomatSelectWhetherToPlaceNewEdgePieceSelf'} :
                    {key: 'strings:game.gameState.actions.diplomatSelectWhetherToPlaceNewEdgePieceOther', options: {playerUsername: input.displayUsername}}
            case ActionState.IntrigueSelectKnightToMove:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.intrigueSelectKnightToMoveSelf'} :
                    {key: 'strings:game.gameState.actions.intrigueSelectKnightToMoveOther', options: {playerUsername: input.displayUsername}}
            case ActionState.SpySelectPlayer:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.spySelectPlayerSelf'} :
                    {key: 'strings:game.gameState.actions.spySelectPlayerOther', options: {playerUsername: input.displayUsername}}
            case ActionState.SpySelectProgressCard:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.spySelectProgressCardSelf'} :
                    {key: 'strings:game.gameState.actions.spySelectProgressCardOther', options: {playerUsername: input.displayUsername}}
            case ActionState.SelectCardsForWedding:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectCardsForWeddingSelf', options: {count: numberOfPlayersTakingAction}} :
                    {key: 'strings:game.gameState.actions.selectCardsForWeddingOther', options: {count: numberOfPlayersTakingAction, playerUsername: input.displayUsername}}
            case ActionState.CommercialHarborSelectPlayerAndResource:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.commercialHarborSelectPlayerAndResourceSelf'} :
                    {key: 'strings:game.gameState.actions.commercialHarborSelectPlayerAndResourceOther', options: {playerUsername: input.displayUsername}}
            case ActionState.CommercialHarborOpponentSelectsCard:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.commercialHarborOpponentSelectsCardSelf'} :
                    {key: 'strings:game.gameState.actions.commercialHarborOpponentSelectsCardOther', options: {playerUsername: input.displayUsername}}
            case ActionState.MasterMerchantSelectPlayer:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.masterMerchantSelectPlayerSelf'} :
                    {key: 'strings:game.gameState.actions.masterMerchantSelectPlayerOther', options: {playerUsername: input.displayUsername}}
            case ActionState.MasterMerchantSelectCards:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.masterMerchantSelectCardsSelf'} :
                    {key: 'strings:game.gameState.actions.masterMerchantSelectCardsOther', options: {playerUsername: input.displayUsername}}
            case ActionState.MerchantFleetSelectResource:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.merchantFleetSelectResourceSelf'} :
                    {key: 'strings:game.gameState.actions.merchantFleetSelectResourceOther', options: {playerUsername: input.displayUsername}}
            case ActionState.ResourceMonopolySelectResource:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.resourceMonopolySelectResourceSelf'} :
                    {key: 'strings:game.gameState.actions.resourceMonopolySelectResourceOther', options: {playerUsername: input.displayUsername}}
            case ActionState.CommodityMonopolySelectResource:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.commodityMonopolySelectResourceSelf'} :
                    {key: 'strings:game.gameState.actions.commodityMonopolySelectResourceOther', options: {playerUsername: input.displayUsername}}
            default:
                return {key: 'strings:colonistCommon.utils.bug'}
        }
    }

    private static getTurnStateMessage(input: CurrentStateMessageInput): TranslationObject {
        if(input.type != 'turn-state-default' && input.type != 'turn-state-turn') return {key: 'strings:colonistCommon.utils.bug'}
        const turnState = input.currentState.turnState
        const amITakingAction = input.amITakingAction == true
        switch(turnState) {
            case TurnState.InitialPlacement:
                return amITakingAction ?
                    {key: 'strings:game.gameState.turn.initialPlacementSelf'} :
                    {key: 'strings:game.gameState.turn.initialPlacementOther', options: {playerUsername: input.playerDisplayUsername}}
            case TurnState.Dice:
                return amITakingAction ?
                    {key: 'strings:game.gameState.turn.diceSelf'} :
                    {key: 'strings:game.gameState.turn.diceOther', options: {playerUsername: input.playerDisplayUsername}}
            case TurnState.Turn:
                try {
                    const turnMessageData = CurrentStateMessageUtils.calculateTurnStateTurnMessageData(input)
                    return CurrentStateMessageUtils.getTurnStateTurnMessage(turnMessageData)
                } catch(error) {
                    return {key: 'strings:colonistCommon.utils.bug'}
                }
            case TurnState.SpecialBuildPhase:
                return amITakingAction ?
                    {key: 'strings:game.gameState.turn.specialBuildPhaseSelf'} :
                    {key: 'strings:game.gameState.turn.specialBuildPhaseOther', options: {playerUsername: input.playerDisplayUsername}}
            case TurnState.GameEnd:
                return amITakingAction ?
                    {key: 'strings:game.gameState.turn.gameEndSelf'} :
                    {key: 'strings:game.gameState.turn.gameEndOther', options: {playerUsername: input.playerDisplayUsername}}
        }
    }

    private static calculateTurnStateTurnMessageData(input: CurrentStateMessageInput): CurrentStateMessageData {
        if(input.type != 'turn-state-turn') throw new Error('Invalid input for TurnState.Turn')

        const arePlayersTrading = input.arePlayersTrading == true
        if(!arePlayersTrading) {
            return {
                type: 'turn-state-without-trade',
                arePlayersTrading: false,
                amITakingAction: input.amITakingAction,
                playerDisplayUsername: input.playerDisplayUsername,
            }
        }

        const amICurrentTurnPlayer = input.currentState.currentTurnPlayerColor == input.myColor
        if(!amICurrentTurnPlayer) {
            return {
                type: 'turn-state-ongoing-trade-in-others-turn',
                arePlayersTrading: true,
                amICurrentTurnPlayer: false,
                amITakingAction: input.amITakingAction,
                numberOfPlayersTakingAction: input.numberOfPlayersTakingAction,
                playerDisplayUsername: input.playerDisplayUsername,
            }
        }

        const otherPlayersTrading = [...input.playersTakingAction]
        ArrayUtils.removeElementFromArray(otherPlayersTrading, input.currentState.currentTurnPlayerColor)
        const numberOtherPlayersTrading = otherPlayersTrading.length

        return {
            type: 'turn-state-ongoing-trade-in-my-turn',
            arePlayersTrading: true,
            amICurrentTurnPlayer: true,
            wasATradeAccepted: input.wasATradeAccepted,
            numberOtherPlayersTrading: numberOtherPlayersTrading,
            otherPlayerDisplayName: input.otherPlayerDisplayName,
        }
    }

    static getPlayerInfoExtraFooterActionStateText(input: {
        turnState: TurnState
        actionState: ActionState
    }): TranslationObject | undefined {
        if(input.turnState === TurnState.SpecialBuildPhase) return {key: 'strings:game.gameState.actions.specialBuildPhase'}

        switch(input.actionState) {
            case ActionState.SelectCardsToDiscard:
            case ActionState.SelectProgressCardsToDiscard: return {key: 'strings:game.gameState.actions.discardingCards'}
            case ActionState.MoveKnightOffTurn: return {key: 'strings:game.gameState.actions.movingKnight'}
            case ActionState.SelectKnightToRemove: return {key: 'strings:game.gameState.actions.removingKnight'}
            case ActionState.SelectCityToPillage: return {key: 'strings:game.gameState.actions.removingCity'}
            case ActionState.CommercialHarborOpponentSelectsCard: return {key: 'strings:game.gameState.actions.selectingResource'}
            case ActionState.SelectCardsForWedding:
            case ActionState.SelectCardsForPendingDistributions: return {key: 'strings:game.gameState.actions.selectingResources'}
            case ActionState.SelectProgressCard: return {key: 'strings:game.gameState.actions.selectingProgressCard'}
            default: return undefined
        }
    }

    private static readonly HIDDEN_OPPONENT_ACTION_STATES = new Set<ActionState>([
        ActionState.Select1ResourceForMonopoly,
        ActionState.Select2ResourcesForYearOfPlenty,
        ActionState.SelectTileProductionNumbersToSwap,
        ActionState.SelectPlayerForDeserter,
        ActionState.MasterMerchantSelectPlayer,
        ActionState.MasterMerchantSelectCards,
        ActionState.MerchantFleetSelectResource,
        ActionState.ResourceMonopolySelectResource,
        ActionState.CommodityMonopolySelectResource,
    ])

    static isActionStateHiddenFromOpponents(actionState: ActionState): boolean {
        return CurrentStateMessageUtils.HIDDEN_OPPONENT_ACTION_STATES.has(actionState)
    }

    private static getTurnStateTurnMessage(data: CurrentStateMessageData): TranslationObject {
        if(!data.arePlayersTrading) {
            return data.amITakingAction ?
                {key: 'strings:game.gameState.turn.turnSelf'} :
                {key: 'strings:game.gameState.turn.turnOther', options: {playerUsername: data.playerDisplayUsername}}
        }

        if(data.amICurrentTurnPlayer) {
            return data.wasATradeAccepted ?
                {key: 'strings:game.gameState.turn.tradingSelf', options: {count: data.numberOtherPlayersTrading}} :
                {key: 'strings:game.gameState.turn.tradingOther', options: {count: data.numberOtherPlayersTrading, playerUsername: data.otherPlayerDisplayName}}
        } else {
            return data.amITakingAction ?
                {key: 'strings:game.gameState.turn.tradingSelf', options: {count: data.numberOfPlayersTakingAction}} :
                {key: 'strings:game.gameState.turn.tradingOther', options: {count: data.numberOfPlayersTakingAction, playerUsername: data.playerDisplayUsername}}
        }
    }
}
